import { mapMutations, mapActions } from 'vuex';
import { getFirstMenuRoute } from '@/libs/utils';
import { buildFileUrl } from '@/utils/fileUrl';
import defaultAvatar from '@/assets/default-avatar.png';
import taskIconUrl from '@/assets/file_task.png';
import menuFoldIconUrl from '@/assets/menu-fold.png';
import schoolIconUrl from '@/assets/school.png';
import GlobalWatermark from '@/components/GlobalWatermark/index.vue';
import LogoutTransition from '@/views/main/components/LogoutTransition/index.vue';
import PageTabs from '@/views/main/components/PageTabs/index.vue';
import SchoolSelectModal from '@/views/main/components/SchoolSelectModal/index.vue';
import UserProfileCard from '@/views/main/components/UserProfileCard/index.vue';
import { resetRouter } from '@/router/index';
import { getUserDetail, getUserSchoolList } from '@/api/user/user.api';
import {
  clearCurrentSchoolInfo,
  getCurrentSchoolInfo,
  normalizeSchoolInfo,
  setCurrentSchoolInfo
} from '@/utils/schoolContext';
import {canteenUserRestrictedMessage,isCanteenUserRouteRestricted} from '@/utils/restrictedAccess';

const MENU_OPEN_ROOT = '__root__'
const PAGE_CACHE_ENABLED_KEY = 'drs-page-cache-enabled'

function getStoredPageCacheEnabled() {
  const value = localStorage.getItem(PAGE_CACHE_ENABLED_KEY)
  return value === null ? true : value === '1'
}

function getStoredUserInfo() {
  try {
    return JSON.parse(localStorage.getItem('userInfo') || '{}') || {}
  } catch (error) {
    return {}
  }
}

function getStoredRoleInfo() {
  try {
    return JSON.parse(localStorage.getItem('roleInfo') || '[]') || []
  } catch (error) {
    return []
  }
}

function shouldSelectSchool(userInfo) {
  return String((userInfo || {}).userType || '') === '01'
}
function canSelectSchool(userInfo) {
  const userType = String((userInfo || {}).userType || '')
  return userType === '00' || userType === '01'
}
function shouldUseBoundSchool(userInfo) {
  return String((userInfo || {}).userType || '') === '02' && !!(userInfo || {}).schoolId
}

export default {
  components: {
    GlobalWatermark,
    LogoutTransition,
    PageTabs,
    SchoolSelectModal,
    UserProfileCard
  },
  data() {
    const storedUserInfo = getStoredUserInfo()
    const storedSchoolInfo = getCurrentSchoolInfo()
    return {
      collapsed: false,
      openKeys: [],
      visitedTabs: [],
      tabHistory: [],
      viewAlive: true,
      refreshKeyMap: {},
      currentUserInfoData: storedUserInfo,
      userProfileLoading: false,
      menuKeyword: '',
      menuSearchFocused: false,
      logoutTransitionVisible: false,
      logoutTimer: null,
      viewRefreshTimer: null,
      pageCacheEnabled: getStoredPageCacheEnabled(),
      cacheViewVersion: 0,
      currentSchoolInfo: storedSchoolInfo,
      schoolList: [],
      schoolLoading: false,
      schoolReady: !shouldSelectSchool(storedUserInfo) || !!storedSchoolInfo.schoolId,
      schoolContextResolved: !shouldSelectSchool(storedUserInfo) || !!storedSchoolInfo.schoolId,
      schoolModalVisible: false,
      schoolKeyword: '',
      taskIconUrl,
      menuFoldIconUrl,
      schoolIconUrl,
      isFullscreen: false
    }
  },
  mounted() {
    this.refreshCurrentUserInfo()
    this.loadCurrentUserDetail()
    this.refreshCurrentSchoolInfo()
    if (this.isSchoolSelectionAvailable) {
      this.loadUserSchools(true)
    } else {
      this.schoolReady = true
      this.schoolContextResolved = true
      this.schoolModalVisible = false
    }
    this.setBreadCrumb(this.$route.matched)
    this.syncMenuByRoute(this.$route)
    this.resetVisitedTabs()
    this.addVisitedTab(this.$route)
    this.scrollActiveTabIntoView(false)
    window.addEventListener('app-close-route-tab', this.handleCloseRouteTab)
    window.addEventListener('app-user-info-updated', this.handleUserInfoUpdated)
    document.addEventListener('fullscreenchange', this.handleFullscreenChange)
    document.addEventListener('webkitfullscreenchange', this.handleFullscreenChange)
    document.addEventListener('mozfullscreenchange', this.handleFullscreenChange)
    document.addEventListener('MSFullscreenChange', this.handleFullscreenChange)
  },
  beforeDestroy() {
    window.removeEventListener('app-close-route-tab', this.handleCloseRouteTab)
    window.removeEventListener('app-user-info-updated', this.handleUserInfoUpdated)
    document.removeEventListener('fullscreenchange', this.handleFullscreenChange)
    document.removeEventListener('webkitfullscreenchange', this.handleFullscreenChange)
    document.removeEventListener('mozfullscreenchange', this.handleFullscreenChange)
    document.removeEventListener('MSFullscreenChange', this.handleFullscreenChange)
    if (this.logoutTimer) window.clearTimeout(this.logoutTimer)
    if (this.viewRefreshTimer) window.clearTimeout(this.viewRefreshTimer)
    this.unlockViewRefreshOverflow()
  },
  methods: {
    ...mapActions([
      'setUserLanguage',
      'clearUserMenus'
    ]),
    ...mapMutations([
      'setBreadCrumb',
      'clearTaskNumber'
    ]),
    breadcrumbIconType(item) {
      const icon = item && item.meta ? String(item.meta.icon || '').trim() : ''
      return icon && /^[a-z][a-z0-9-]*$/.test(icon) ? icon : ''
    },
    handleLanguageChange(e) {
      this.$i18n.locale = e.key
      localStorage.setItem('lang', e.key)
      this.setUserLanguage({ userLang: e.key })
    },
    handleUserMenuClick(e) {
      if (e.key === 'logout') {
        this.logout()
        return
      }
      const query = e.key === 'password' ? { type: 'password' } : {}
      this.$router.push({ name: 'SystemProfile', query }).catch(err => {
        err
      })
    },
    openUserProfile(type) {
      const query = type === 'password' ? { type: 'password' } : {}
      this.$router.push({ name: 'SystemProfile', query }).catch(err => {
        err
      })
    },
    formatUserTime(value) {
      return value ? String(value).replace('T', ' ').slice(0, 16) : '--'
    },
    refreshCurrentUserInfo() {
      this.currentUserInfoData = getStoredUserInfo()
      this.syncSchoolSelectionRequirement()
    },
    loadCurrentUserDetail() {
      if (this.userProfileLoading) return
      const storedUser = getStoredUserInfo()
      const userId = this.currentUserInfoData.userId || storedUser.userId
      if (!userId) return
      this.userProfileLoading = true
      getUserDetail(userId).then(res => {
        if (res.code !== 200) return
        const userInfo = Object.assign({}, storedUser, res.data || {})
        localStorage.setItem('userInfo', JSON.stringify(userInfo))
        this.currentUserInfoData = userInfo
        this.syncSchoolSelectionRequirement()
      }).catch(error => {
        console.error(error)
      }).finally(() => {
        this.userProfileLoading = false
      })
    },
    handleUserInfoUpdated(event) {
      this.currentUserInfoData = (event && event.detail) || {}
      this.syncSchoolSelectionRequirement()
    },
    syncSchoolSelectionRequirement() {
      if (this.isSchoolSelectionRequired) return
      if (this.isSchoolSelectionAvailable) {
        this.schoolReady = true
        return
      }
      if (shouldUseBoundSchool(this.currentUserInfoData)) {
        const boundSchoolInfo = {
          schoolId: this.currentUserInfoData.schoolId,
          schoolName: this.currentUserInfoData.schoolName || this.currentUserInfoData.school || ''
        }
        this.currentSchoolInfo = boundSchoolInfo
        this.$store.commit('setCurrentSchoolInfo', boundSchoolInfo)
        localStorage.setItem('schoolId', boundSchoolInfo.schoolId)
        localStorage.setItem('schoolInfo', JSON.stringify(boundSchoolInfo))
        this.schoolReady = true
        this.schoolContextResolved = true
        this.schoolModalVisible = false
        return
      }
      clearCurrentSchoolInfo()
      this.currentSchoolInfo = {}
      this.schoolReady = true
      this.schoolContextResolved = true
      this.schoolModalVisible = false
    },
    refreshCurrentSchoolInfo() {
      this.currentSchoolInfo = getCurrentSchoolInfo()
    },
    normalizeSchoolList(list) {
      return (list || []).map(item => normalizeSchoolInfo(item)).filter(item => item.schoolId)
    },
    loadUserSchools(autoEnsure = true) {
      if (!this.isSchoolSelectionAvailable) {
        clearCurrentSchoolInfo()
        this.currentSchoolInfo = {}
        this.schoolReady = true
        this.schoolContextResolved = true
        this.schoolModalVisible = false
        return Promise.resolve()
      }
      if (autoEnsure && !this.currentSchoolInfo.schoolId) {
        this.schoolContextResolved = false
      }
      this.schoolLoading = true
      return getUserSchoolList({
        current: 1,
        size: 1000
      }).then(res => {
        if (res.code !== 200) {
          this.$message.error(res.msg || '学校列表查询失败')
          return
        }
        const data = res.data || {}
        this.schoolList = this.normalizeSchoolList(data.records || [])
        if (autoEnsure) {
          this.ensureSelectedSchool()
        }
      }).catch(error => {
        console.error(error)
      }).finally(() => {
        this.schoolLoading = false
        if (autoEnsure) this.schoolContextResolved = true
      })
    },
    ensureSelectedSchool() {
      if (!this.schoolList.length) {
        clearCurrentSchoolInfo()
        this.currentSchoolInfo = {}
        this.schoolReady = false
        this.schoolModalVisible = String(this.currentUserInfoData.userType || '') === '00'
        return
      }
      const currentId = this.currentSchoolInfo.schoolId
      const matched = currentId ? this.schoolList.find(item => item.schoolId === currentId) : null
      if (matched) {
        this.currentSchoolInfo = setCurrentSchoolInfo(matched)
        this.schoolReady = true
        return
      }
      if (currentId && this.schoolKeyword) return
      if (currentId) {
        clearCurrentSchoolInfo()
        this.currentSchoolInfo = {}
        this.schoolReady = false
      }
      if (this.schoolList.length === 1 && this.isSchoolSelectionRequired) {
        this.currentSchoolInfo = setCurrentSchoolInfo(this.schoolList[0])
        this.schoolReady = true
        return
      }
      this.schoolReady = false
      this.schoolModalVisible = true
    },
    handleSchoolSearch() {
      this.schoolKeyword = String(this.schoolKeyword || '').trim()
    },
    openSchoolModal() {
      if (!this.isSchoolSelectionAvailable) return
      this.schoolModalVisible = true
      if (!this.schoolLoading) {
        this.loadUserSchools(false)
      }
    },
    closeSchoolModal() {
      if (this.isSchoolSelectionRequired && !this.currentSchoolInfo.schoolId) return
      this.schoolModalVisible = false
      this.schoolKeyword = ''
    },
    selectSchool(school) {
      const next = setCurrentSchoolInfo(school)
      const changed = next.schoolId !== this.currentSchoolInfo.schoolId
      this.currentSchoolInfo = next
      this.schoolReady = !!next.schoolId
      this.schoolContextResolved = true
      this.schoolModalVisible = false
      this.schoolKeyword = ''
      this.refreshCurrentUserInfo()
      if (changed) {
        this.$message.success('已切换学校')
        this.refreshCurrentView()
      }
    },
    logout() {
      if (this.logoutTransitionVisible) return
      this.logoutTransitionVisible = true
      this.logoutTimer = window.setTimeout(() => {
        this.finishLogout()
      }, 1100)
    },
    finishLogout() {
      localStorage.removeItem('token')
      localStorage.removeItem('userInfo')
      localStorage.removeItem('roleInfo')
      localStorage.removeItem('schoolId')
      clearCurrentSchoolInfo()
      sessionStorage.removeItem('visitedTabs')
      this.clearUserMenus()
      resetRouter()
      this.$router.replace({ name: 'login' }).catch(err => {
        err
      }).finally(() => {
        this.logoutTransitionVisible = false
        this.logoutTimer = null
      })
    },
    handleAvatarError(e) {
      if (e && e.target && e.target.src !== defaultAvatar) {
        e.target.src = defaultAvatar
      }
    },
    showBreadcrumbItem(item) {
      return (item.meta && item.meta.title) || item.name
    },
    getMenuTitle(item) {
      return item && item.meta && item.meta.title ? String(item.meta.title) : ''
    },
    getMenuTarget(item) {
      if (!item) return null
      if (item.fullPath) return item.fullPath
      if (item.path && item.path.charAt(0) === '/') return item.path
      if (item.name) return { name: item.name }
      return null
    },
    isMenuIconVisible(item) {
      const icon = item && item.meta ? String(item.meta.icon || '').trim() : ''
      if (!icon || icon === '#' || icon.indexOf('fa ') === 0 || icon.indexOf(' ') > -1) {
        return false
      }
      return /^[a-z][a-z0-9-]*$/.test(icon)
    },
    flattenMenuSearchItems(list, parents) {
      return (list || []).reduce((result, item) => {
        const currentParents = (parents || []).concat(item)
        const title = this.getMenuTitle(item)
        const target = this.getMenuTarget(item)
        if (title && target && (!item.children || !item.children.length)) {
          result.push({
            name: item.name,
            title,
            labelPath: currentParents.map(menu => this.getMenuTitle(menu)).filter(Boolean).join('>'),
            fullPath: item.fullPath,
            path: item.path
          })
        }
        if (item.children && item.children.length) {
          result = result.concat(this.flattenMenuSearchItems(item.children, currentParents))
        }
        return result
      }, [])
    },
    handleMenuSearchBlur() {
      window.setTimeout(() => {
        this.menuSearchFocused = false
      }, 120)
    },
    handleMenuSearchEnter() {
      if (this.menuSearchResults.length) {
        this.handleMenuSearchSelect(this.menuSearchResults[0])
      }
    },
    handleMenuSearchSelect(item) {
      this.menuKeyword = ''
      this.menuSearchFocused = false
      const target = this.getMenuTarget(item)
      if (!target) return
      this.$router.push(target).catch(err => {
        err
      })
    },
    findMenuItemByName(list, name) {
      let i = -1
      const len = list ? list.length : 0
      while (++i < len) {
        const item = list[i]
        if (item.name === name) return item
        if (item.children && item.children.length) {
          const child = this.findMenuItemByName(item.children, name)
          if (child) return child
        }
      }
      return null
    },
    getMenuOpenInfo() {
      const parentMap = {}
      const submenuMap = {}
      const walk = (list, parentKey) => {
        ;(list || []).forEach(item => {
          if (!item || !item.name) return
          if (item.children && item.children.length) {
            parentMap[item.name] = parentKey || MENU_OPEN_ROOT
            submenuMap[item.name] = true
            walk(item.children, item.name)
          }
        })
      }
      walk(this.menuList, MENU_OPEN_ROOT)
      return { parentMap, submenuMap }
    },
    isOpenAncestorChain(key, latestKeyByParent, parentMap) {
      let parentKey = parentMap[key]
      while (parentKey && parentKey !== MENU_OPEN_ROOT) {
        const grandParentKey = parentMap[parentKey] || MENU_OPEN_ROOT
        if (latestKeyByParent[grandParentKey] !== parentKey) {
          return false
        }
        parentKey = grandParentKey
      }
      return true
    },
    normalizeOpenKeys(openKeys) {
      const keys = Array.isArray(openKeys) ? openKeys : []
      const { parentMap, submenuMap } = this.getMenuOpenInfo()
      const latestKeyByParent = {}

      keys.forEach(key => {
        if (!submenuMap[key]) return
        latestKeyByParent[parentMap[key] || MENU_OPEN_ROOT] = key
      })

      return keys.filter(key => {
        const parentKey = parentMap[key] || MENU_OPEN_ROOT
        return submenuMap[key] &&
          latestKeyByParent[parentKey] === key &&
          this.isOpenAncestorChain(key, latestKeyByParent, parentMap)
      })
    },
    handleOpenChange(openKeys) {
      this.openKeys = this.normalizeOpenKeys(openKeys)
    },
    goHome() {
      const firstMenuRoute = getFirstMenuRoute(this.menuList)
      if (!firstMenuRoute || !firstMenuRoute.name) return
      if (this.$route && this.$route.name === firstMenuRoute.name) return
      this.$router.push(firstMenuRoute.fullPath || firstMenuRoute.path || { name: firstMenuRoute.name }).catch(err => {
        err
      })
    },
    scrollActiveTabIntoView(smooth = true) {
      const component = this.$refs.pageTabs
      if (component && component.scrollActiveTabIntoView) {
        component.scrollActiveTabIntoView(smooth)
      }
    },
    toggleCollapsed() {
      this.collapsed = !this.collapsed
      if (this.collapsed) {
        this.openKeys = []
      } else {
        this.syncMenuByRoute(this.$route)
      }
      window.setTimeout(() => {
        this.scrollActiveTabIntoView(false)
      }, 320)
    },
    syncMenuByRoute(route) {
      if (this.collapsed || !route || !route.matched || route.matched.length < 2) {
        return
      }
      this.openKeys = route.matched.slice(0, route.matched.length - 1)
        .filter(item => item.name)
        .map(item => item.name)
      this.openKeys = this.normalizeOpenKeys(this.openKeys)
    },
    handleClick(e) {
      const menuItem = this.findMenuItemByName(this.menuList, e.key)
      this.$router.push(menuItem && menuItem.fullPath ? menuItem.fullPath : {
        name: e.key
      }).catch(err => {
        err
      })
    },
    openExportTaskList() {
      this.clearTaskNumber()
      if (this.$route && this.$route.name === 'SystemExportList') return
      this.$router.push({
        name: 'SystemExportList'
      }).catch(err => {
        err
      })
    },
    routeTitle(route) {
      return route && route.meta && route.meta.title ? route.meta.title : route.name
    },
    routeIcon(route) {
      const icon = route && route.meta ? String(route.meta.icon || '').trim() : ''
      if (icon && /^[a-z][a-z0-9-]*$/.test(icon)) return icon
      const menuItem = this.findMenuItemByName(this.menuList, route && route.name)
      const menuIcon = menuItem && menuItem.meta ? String(menuItem.meta.icon || '').trim() : ''
      return menuIcon && /^[a-z][a-z0-9-]*$/.test(menuIcon) ? menuIcon : ''
    },
    routeFullPath(route) {
      if (!route) return ''
      if (route.fullPath) return route.fullPath
      if (route.path && route.path.charAt(0) === '/') return route.path
      const resolved = this.$router.resolve({ name: route.name })
      return resolved && resolved.route ? resolved.route.fullPath : ''
    },
    isTabRoute(route) {
      return route && route.name && route.meta && route.meta.requireAuth && !route.redirect
    },
    resetVisitedTabs() {
      this.visitedTabs = []
      this.tabHistory = []
      this.saveVisitedTabs()
    },
    saveVisitedTabs() {
      sessionStorage.setItem('visitedTabs', JSON.stringify(this.visitedTabs))
    },
    addVisitedTab(route) {
      if (!this.isTabRoute(route)) return
      const exist = this.visitedTabs.some(item => item.name === route.name)
      if (!exist) {
        this.visitedTabs.push({
          name: route.name,
          title: this.routeTitle(route),
          icon: this.routeIcon(route),
          fullPath: route.fullPath,
          closable: true
        })
      } else {
        this.visitedTabs = this.visitedTabs.map(item => {
          if (item.name === route.name) {
            return Object.assign({}, item, {
              title: this.routeTitle(route),
              icon: this.routeIcon(route),
              fullPath: route.fullPath
            })
          }
          return item
        })
      }
      this.syncTabClosable()
      this.pushTabHistory(route.name)
      this.saveVisitedTabs()
    },
    ensureVisitedTab(route) {
      if (!this.isTabRoute(route)) return
      const exist = this.visitedTabs.some(item => item.name === route.name)
      if (!exist) {
        this.visitedTabs.push({
          name: route.name,
          title: this.routeTitle(route),
          icon: this.routeIcon(route),
          fullPath: this.routeFullPath(route),
          closable: true
        })
      }
      this.syncTabClosable()
      this.pushTabHistory(route.name)
      this.saveVisitedTabs()
    },
    pushTabHistory(name) {
      if (!name) return
      this.tabHistory = this.tabHistory.filter(item => item !== name)
      this.tabHistory.push(name)
    },
    syncTabClosable() {
      const shouldClosable = this.visitedTabs.length > 1
      this.visitedTabs = this.visitedTabs.map(item => Object.assign({}, item, {
        closable: shouldClosable
      }))
    },
    openVisitedTab(tag) {
      if (tag.name === this.currentTabName) return
      this.$router.push(tag.fullPath || { name: tag.name }).catch(err => {
        err
      })
    },
    closeVisitedTab(tag) {
      const index = this.visitedTabs.findIndex(item => item.name === tag.name)
      if (index === -1 || !tag.closable) return
      this.removeVisitedTabs([tag.name])
      if (tag.name === this.currentTabName) {
        this.openFallbackTab()
      }
    },
    closeRouteTab(name, target, refresh) {
      if (target && target.name) {
        const routeTarget = Object.assign({}, target)
        delete routeTarget.refresh
        this.$router.push(routeTarget).then(() => {
          if (name) {
            this.removeVisitedTabs([name])
          }
          if (refresh) {
            this.$nextTick(() => {
              this.refreshCurrentView()
            })
          }
        }).catch(err => {
          err
        })
        return
      }
      if (name) {
        this.removeVisitedTabs([name])
      }
      if (name === this.currentTabName) {
        this.openFallbackTab()
      }
    },
    handleCloseRouteTab(event) {
      const detail = event && event.detail ? event.detail : {}
      this.closeRouteTab(detail.name, detail.target, detail.refresh)
    },
    removeVisitedTabs(names) {
      const removeNames = names.filter(Boolean)
      const removedTabs = this.visitedTabs.filter(item => removeNames.indexOf(item.name) > -1)
      this.visitedTabs = this.visitedTabs.filter(item => removeNames.indexOf(item.name) === -1)
      this.tabHistory = this.tabHistory.filter(name => removeNames.indexOf(name) === -1)
      this.invalidateRouteCaches(removedTabs)
      this.syncTabClosable()
      this.saveVisitedTabs()
      this.scrollActiveTabIntoView()
    },
    invalidateRouteCaches(tabs) {
      ; (tabs || []).forEach(tab => {
        const key = tab.fullPath || tab.name
        if (key) {
          this.$set(this.refreshKeyMap, key, (this.refreshKeyMap[key] || 0) + 1)
        }
      })
    },
    openFallbackTab() {
      const previousName = this.tabHistory[this.tabHistory.length - 1]
      const previousTag = previousName ? this.visitedTabs.find(item => item.name === previousName) : null
      const fallbackTag = previousTag || this.visitedTabs[this.visitedTabs.length - 1]
      if (fallbackTag) {
        this.openVisitedTab(fallbackTag)
      } else {
        this.$router.push('/').catch(err => {
          err
        })
      }
    },
    closeOtherTabs(tag) {
      if (!tag) return
      const removedTabs = this.visitedTabs.filter(item => item.name !== tag.name)
      this.visitedTabs = this.visitedTabs.filter(item => item.name === tag.name)
      this.tabHistory = this.tabHistory.filter(name => name === tag.name)
      this.invalidateRouteCaches(removedTabs)
      this.syncTabClosable()
      this.saveVisitedTabs()
      this.scrollActiveTabIntoView()
      if (tag.name !== this.currentTabName) {
        this.openVisitedTab(tag)
      }
    },
    closeAllTabs() {
      const firstMenuRoute = getFirstMenuRoute(this.menuList)
      const targetRoute = firstMenuRoute && firstMenuRoute.name ? firstMenuRoute : this.$route
      this.invalidateRouteCaches(this.visitedTabs)
      this.visitedTabs = []
      this.tabHistory = []
      this.ensureVisitedTab(targetRoute)
      if (this.$route && this.$route.name === targetRoute.name) {
        this.scrollActiveTabIntoView()
        return
      }
      this.$router.push(targetRoute.fullPath || targetRoute.path || { name: targetRoute.name }).then(() => {
        this.ensureVisitedTab(this.$route)
      }).catch(err => {
        err
      })
    },
    refreshTab(tag) {
      if (tag && tag.name !== this.currentTabName) {
        this.openVisitedTab(tag)
        this.$nextTick(() => {
          this.refreshCurrentView()
        })
        return
      }
      this.refreshCurrentView()
    },
    refreshCurrentView() {
      this.lockViewRefreshOverflow()
      this.viewAlive = false
      const key = this.routeCacheKey(this.$route)
      this.$set(this.refreshKeyMap, key, (this.refreshKeyMap[key] || 0) + 1)
      this.$nextTick(() => {
        this.viewAlive = true
        if (this.viewRefreshTimer) window.clearTimeout(this.viewRefreshTimer)
        this.viewRefreshTimer = window.setTimeout(() => {
          this.unlockViewRefreshOverflow()
          this.viewRefreshTimer = null
        }, 260)
      })
    },
    lockViewRefreshOverflow() {
      if (typeof document === 'undefined') return
      document.documentElement.classList.add('drs-view-refreshing')
      document.body.classList.add('drs-view-refreshing')
    },
    unlockViewRefreshOverflow() {
      if (typeof document === 'undefined') return
      document.documentElement.classList.remove('drs-view-refreshing')
      document.body.classList.remove('drs-view-refreshing')
    },
    handleFullscreenChange() {
      this.isFullscreen = !!(document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement || document.msFullscreenElement)
    },
    toggleFullscreen() {
      const fullscreenElement = document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement || document.msFullscreenElement
      if (fullscreenElement) {
        const exitFullscreen = document.exitFullscreen || document.webkitExitFullscreen || document.mozCancelFullScreen || document.msExitFullscreen
        if (exitFullscreen) exitFullscreen.call(document)
        return
      }
      const element = document.documentElement
      const requestFullscreen = element.requestFullscreen || element.webkitRequestFullscreen || element.mozRequestFullScreen || element.msRequestFullscreen
      if (requestFullscreen) requestFullscreen.call(element)
    },
    routeCacheKey(route) {
      return route && route.fullPath ? route.fullPath : route.name
    },
    viewKey(route) {
      const key = this.routeCacheKey(route)
      return `${key}:${this.refreshKeyMap[key] || 0}`
    },
    handleTabMenuClick(e, tag) {
      const action = e.key
      if (action === 'refresh') {
        this.refreshTab(tag)
        return
      }
      if (action === 'pageCache') {
        return
      }
      if (action === 'closeCurrent' && tag) {
        this.closeVisitedTab(tag)
        return
      }
      if (action === 'closeOthers' && tag) {
        this.closeOtherTabs(tag)
        return
      }
      if (action === 'closeAll') {
        this.closeAllTabs()
      }
    },
    handlePageCacheChange(checked) {
      this.pageCacheEnabled = checked
      localStorage.setItem(PAGE_CACHE_ENABLED_KEY, checked ? '1' : '0')
      this.cacheViewVersion += 1
      this.refreshCurrentView()
    }
  },
  watch: {
    '$route'(newRoute) {
      this.setBreadCrumb(newRoute.matched)
      this.syncMenuByRoute(newRoute)
      this.addVisitedTab(newRoute)
      this.scrollActiveTabIntoView()
    }
  },
  computed: {
    breadCrumbList() {
      return this.$store.state.app.breadCrumbList
    },
    taskNumber() {
      return this.$store.getters.taskNumber
    },
    menuList() {
      return this.$store.getters.menuList;
    },
    normalizedMenuKeyword() {
      return String(this.menuKeyword || '').trim().toLowerCase()
    },
    menuSearchSource() {
      return this.flattenMenuSearchItems(this.menuList, [])
    },
    menuSearchResults() {
      if (!this.normalizedMenuKeyword) {
        return []
      }
      return this.menuSearchSource.filter(item => {
        return item.title.toLowerCase().indexOf(this.normalizedMenuKeyword) > -1
          || item.labelPath.toLowerCase().indexOf(this.normalizedMenuKeyword) > -1
      }).slice(0, 12)
    },
    showMenuSearchPanel() {
      return !this.collapsed && this.menuSearchFocused && !!this.normalizedMenuKeyword
    },
    isSchoolSelectionRequired() {
      return shouldSelectSchool(this.currentUserInfoData)
    },
    isSchoolSelectionAvailable() {
      return canSelectSchool(this.currentUserInfoData)
    },
    canRenderCurrentView() {
      return !this.isSchoolSelectionRequired || (this.schoolReady && !!this.currentSchoolInfo.schoolId)
    },
    showCanteenUserRestrictedMask() {
      return isCanteenUserRouteRestricted(this.$route, this.currentUserInfoData)
    },
    canteenUserRestrictedMessage() {
      return canteenUserRestrictedMessage
    },
    currentSchoolName() {
      return this.currentSchoolInfo.schoolName || ''
    },
    watermarkVisible() {
      return !!localStorage.getItem('token')
        && this.schoolContextResolved
        && !!this.currentSchoolInfo.schoolId
    },
    watermarkContent() {
      return [this.currentSchoolName]
    },
    filteredSchoolList() {
      const keyword = String(this.schoolKeyword || '').trim().toLowerCase()
      if (!keyword) return this.schoolList
      return this.schoolList.filter(item => {
        return String(item.schoolName || '').toLowerCase().indexOf(keyword) > -1
          || String(item.schoolId || '').toLowerCase().indexOf(keyword) > -1
      })
    },
    navigationBreadCrumbList() {
      const list = this.breadCrumbList.slice()
      const activeMenu = this.$route && this.$route.meta && this.$route.meta.activeMenu
      if (!activeMenu || list.some(item => item.name === activeMenu)) {
        return list
      }
      const activeMenuItem = this.findMenuItemByName(this.menuList, activeMenu)
      if (!activeMenuItem || !list.length) {
        return list
      }
      list.splice(list.length - 1, 0, {
        icon: activeMenuItem.icon,
        name: activeMenuItem.name,
        meta: activeMenuItem.meta
      })
      return list
    },
    selectedKeys() {
      const activeMenu = this.$route && this.$route.meta && this.$route.meta.activeMenu
      return activeMenu || (this.$route && this.$route.name) ? [activeMenu || this.$route.name] : []
    },
    currentTabName() {
      return this.$route && this.$route.name
    },
    currentTab() {
      return this.visitedTabs.find(item => item.name === this.currentTabName)
    },
    routeKeepAlive() {
      return this.pageCacheEnabled && !(this.$route && this.$route.meta && this.$route.meta.keepAlive === false)
    },
    userDisplayName() {
      return this.currentUserInfoData.username || this.currentUserInfoData.account || '当前用户'
    },
    userRoleLabel() {
      const roleInfo = getStoredRoleInfo()
      const role = Array.isArray(roleInfo) ? roleInfo[0] : roleInfo
      return (role && (role.roleName || role.name)) || (String(this.currentUserInfoData.userType || '') === '00' ? '系统用户' : '普通用户')
    },
    userProfileRows() {
      const user = this.currentUserInfoData || {}
      return [
        { icon: 'user', label: '用户名称', value: user.username || '--' },
        { icon: 'idcard', label: '登录账号', value: user.account || '--' },
        { icon: 'phone', label: '手机号码', value: user.phone || '--' },
        { icon: 'mail', label: '用户邮箱', value: user.email || '--' },
        { icon: 'apartment', label: '所在区域', value: user.ancestorsName || user.deptName || '--' },
        { icon: 'calendar', label: '创建日期', value: this.formatUserTime(user.createTime) }
      ]
    },
    userAvatarUrl() {
      return buildFileUrl(this.currentUserInfoData.avatar) || defaultAvatar
    }
  }
}



// WEBPACK FOOTER //
// ./src/views/main/index.js